// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { GainLoss } from '@advisor-app/components';
import { Tooltip } from '@mui/material';
import { ChartContainer } from '@shared/ui-kit/components';
import type { FC } from 'react';
import type { DisplayTag } from '../hooks/use-tag-composition';
import { useTagComposition } from '../hooks/use-tag-composition';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { formatToPercent } from '@shared/ui-kit';

export const TagComposition: FC = () => {
  const MAX_DISPLAY_TAGS = 14;

  const { displayTags, hasNoTags } = useTagComposition();

  const tags = displayTags
    .slice(0, MAX_DISPLAY_TAGS)
    .filter(tag => tag.investmentPercentage > 0);

  return (
    <ChartContainer
      header={<Translation i18nKey="dashboard.tag-composition" ns="dashboard" />}
      popupName="dashboard.tagComposition"
      tooltip
    >
      <div className="grid gap-2 text-border-dark" role="table">
        {hasNoTags ? <NoTagsMessage /> : <TagList tags={tags} />}
      </div>
    </ChartContainer>
  );
};

const TagList: FC<{ tags: DisplayTag[] }> = ({ tags }) => (
  <>
    <div className="mb-1 grid grid-cols-[2fr_1fr_0.5fr_2.5fr] gap-2">
      <div className="col-span-2">
        <Translation i18nKey="dashboard.tags" ns="dashboard" />
      </div>
      <div>%</div>
      <div className="text-right">
        <Translation i18nKey="dashboard.capital-gain" ns="dashboard" />
      </div>
    </div>
    {tags.map(tag => (
      <div
        className="grid grid-cols-[2fr_1fr_0.5fr_2.5fr] items-center gap-2 leading-6"
        key={tag.name}
        role="row"
      >
        <Tooltip title={tag.name} placement="top">
          <div className="line-clamp-1">{tag.name}</div>
        </Tooltip>
        <progress className="progress" value={tag.progressBarValue} />
        <div className="!money-display">
          {formatToPercent(tag.investmentPercentage)}
        </div>
        <GainLoss
          percentage={tag.unrealizedCapitalGainsPercentage || 0}
          value={tag.unrealizedCapitalGains}
          className="text-right"
        />
      </div>
    ))}
  </>
);

const NoTagsMessage = () => (
  <div className="mx-auto mt-5 space-y-4 text-center" data-testid="no-tags-message">
    <p>
      <Translation i18nKey="dashboard.no-tags-message" ns="dashboard" />
    </p>
  </div>
);
